import styled from 'styled-components';
import {
  BodyText,
  FlexColumn,
  FlexRow,
  MainMenu,
  MainMenuItem,
  SvgIcon
} from '@make-software/cspr-design';
import {
  HOME_PATH,
  APPS_PATH,
  USERS_MANAGEMENT_PATH,
  APPS_CREATE_PATH,
  STATISTICS_PATH,
  MARKETPLACE_PATH
} from 'app/route/paths';
import UsersIcon from 'assets/icons/ic-users.svg';
import UserIcon from 'assets/icons/ic-user.svg';
import LogOutIcon from 'assets/icons/ic-logout.svg';
import React from 'react';
import ExploreCatalogIcon from 'assets/icons/ic-sidebar-explore.svg';
import StatisticIcon from 'assets/icons/ic-sidebar-statistic.svg';
import NewAppIcon from 'assets/icons/ic-sidebar-plus-icon.svg';
import { generatePath, NavLink, useNavigate } from 'react-router-dom';
import AppIcon from 'assets/icons/ic-sidebar-app.svg';
import { App, getAppsList, PaginationResponseType } from 'app/console-api';

const MainMenuDesktopContainer = styled.div(({ theme }) =>
  theme.withMedia({
    position: 'relative',
    padding: 0,
    margin: 0,
    width: 'auto',
    '*': {
      boxSizing: 'border-box',
      background: theme.styleguideColors.contentOnFill
    },

    '& li': {
      fontSize: '14px',
      color: theme.styleguideColors.contentPrimary,

      ':last-child': {
        paddingRight: 0
      },
      '&:hover': {
        cursor: 'pointer',
        path: {
          fill: theme.styleguideColors.backgroundQuaternary
        },
        span: {
          color: theme.styleguideColors.backgroundQuaternary
        }
      }
    }
  })
);

const MainMenuMobileContainer = styled.div(({ theme }) =>
  theme.withMedia({
    position: 'relative',
    padding: 0,
    margin: 0,
    width: 'auto',
    '*': {
      boxSizing: 'border-box'
    },

    '& li': {
      fontSize: '14px',

      ':last-child': {
        paddingRight: 0
      }
    }
  })
);

const MobileMenuContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    background: theme.styleguideColors.backgroundTertiary,
    justifyContent: 'space-between',
    alignItems: 'baseline',
    height: 'calc(100svh - 70px)',
    color: 'red',
    padding: '0px',
    margin: 0,
    position: 'fixed',
    left: 0,
    top: 71,
    width: '100%',
    zIndex: 1
  })
);

const LogOutSection = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    borderTop: '1px solid #D2D4D933',
    width: '100%',
    padding: '16px 32px',
    justifyContent: 'space-between',
    alignItems: 'center'
  })
);

const NavItem = styled(NavLink)(({ theme }) =>
  theme.withMedia({
    color: theme.styleguideColors.contentQuaternary,
    display: 'flex',
    gap: '8px',
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '24px',
    alignItems: 'center',
    path: {
      fill: theme.styleguideColors.contentTertiary
    },
    '&:hover': {
      cursor: 'pointer',
      path: {
        fill: theme.styleguideColors.contentLightBlue
      },
      color: theme.styleguideColors.contentOnFill
    },
    '&[aria-current="page"]': {
      color: theme.styleguideColors.contentOnFill,
      path: {
        fill: theme.styleguideColors.contentLightBlue
      }
    }
  })
);

const NavItemTitle = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    width: '300px',
    fontSize: '16px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })
);

const NavItemContainer = styled(FlexRow)(({ theme }) => ({
  padding: '0 32px',
  marginTop: '40px',
  gap: '73px'
}));

const StyledDivider = styled.div(({ theme }) =>
  theme.withMedia({
    borderBottom: `1px solid ${theme.styleguideColors.contentSecondary}`,
    width: '324px',
    position: 'absolute',
    top: '123px'
  })
);

const StyledAppDivider = styled.div(({ theme }) =>
  theme.withMedia({
    borderBottom: `1px solid ${theme.styleguideColors.contentSecondary}`,
    width: '324px',
    position: 'absolute',
    bottom: '-40px'
  })
);

const StyledNavRow = styled(FlexRow)(({ theme }) => ({
  position: 'relative'
}));

const ApplicationWrapper = styled(FlexColumn)(({ theme }) => ({
  position: 'relative',
  gap: '24px'
}));

const StyledNewAppRow = styled(FlexRow)(({ theme }) => ({
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const StyledAddNew = styled(FlexRow)(({ theme }) => ({
  backgroundColor: '#1B254A',
  padding: '6px',
  cursor: 'pointer',
  borderRadius: '4px',
  ':hover': {
    backgroundColor: '#2e3a67'
  }
}));

const NavTitle = styled.div(({ theme }) => ({
  fontSize: '13px',
  fontWeight: '400',
  lineHeight: '16px',
  color: theme.styleguideColors.contentTertiary,
  textTransform: 'uppercase'
}));

interface NavigationMenuProps {
  email: string;
  handleLogout: () => void;
}

export const DesktopNavigationMenu = ({ email, handleLogout }: NavigationMenuProps) => {
  return (
    <MainMenuDesktopContainer>
      <MainMenu>
        <MainMenuItem>
          <SvgIcon src={UserIcon} marginRight />
          <span>{email}</span>
        </MainMenuItem>
        <MainMenuItem>
          <SvgIcon src={LogOutIcon} marginRight />
          <span onClick={handleLogout}>Log out</span>
        </MainMenuItem>
      </MainMenu>
    </MainMenuDesktopContainer>
  );
};

export const MobileNavigationMenu = ({
  email,
  handleLogout,
  setMenuVisible
}: NavigationMenuProps & { setMenuVisible: (isOpen: boolean) => void }) => {
  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  const navigate = useNavigate();

  const handleCreateTaskRedirect = () => {
    handleCloseMenu();
    navigate(APPS_CREATE_PATH);
  };

  const [appsList, setAppsList] = React.useState<PaginationResponseType<App> | null>(null);

  React.useEffect(() => {
    getAppsList({ currentPage: 1, pageSize: Number(process.env.REACT_APP_PAGE_SIZE_ITEMS) }).then(
      (response) => setAppsList(response)
    );
  }, []);

  return (
    <MainMenuMobileContainer>
      <MobileMenuContainer justify="space-between">
        <NavItemContainer direction={'column'}>
          <StyledNavRow gap={40} direction={'column'}>
            <NavItem to={MARKETPLACE_PATH} onClick={handleCloseMenu}>
              <SvgIcon src={ExploreCatalogIcon} size={16} />
              <NavItemTitle size={3}>Marketplace</NavItemTitle>
            </NavItem>

            <NavItem to={STATISTICS_PATH} onClick={handleCloseMenu}>
              <SvgIcon src={StatisticIcon} size={16} />
              <NavItemTitle size={3}>Analytics</NavItemTitle>
            </NavItem>
            <StyledDivider></StyledDivider>
          </StyledNavRow>
          <FlexRow gap={40} direction={'column'}>
            <StyledNewAppRow>
              <NavItem to={''}>
                <NavTitle>Applications</NavTitle>
              </NavItem>
              <StyledAddNew>
                <SvgIcon onClick={handleCreateTaskRedirect} src={NewAppIcon} size={16} />
              </StyledAddNew>
            </StyledNewAppRow>

            <ApplicationWrapper>
              {appsList &&
                appsList.data?.map((app) => (
                  <NavItem
                    to={`${generatePath(APPS_PATH, { appID: app.id })}`}
                    key={app.id}
                    state={{ app: app }}
                  >
                    <SvgIcon src={AppIcon} size={16} />
                    <NavItemTitle onClick={handleCloseMenu} size={3}>
                      {app.name}
                    </NavItemTitle>
                  </NavItem>
                ))}
              <StyledAppDivider />
            </ApplicationWrapper>
          </FlexRow>
          <FlexRow gap={40} direction={'column'}>
            <NavItem to={USERS_MANAGEMENT_PATH} onClick={handleCloseMenu}>
              <SvgIcon src={UsersIcon} size={16} />
              <NavItemTitle size={3}>Accounts</NavItemTitle>
            </NavItem>
          </FlexRow>
        </NavItemContainer>
        <LogOutSection>
          <FlexRow align={'center'}>
            <SvgIcon src={UserIcon} marginRight />
            <BodyText scale={'sm'} size={3} variation={'lightGray'}>
              {email}
            </BodyText>
          </FlexRow>
          <SvgIcon src={LogOutIcon} onClick={handleLogout} />
        </LogOutSection>
      </MobileMenuContainer>
    </MainMenuMobileContainer>
  );
};
